/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/invoiceSettings.scss';

import React, {MouseEventHandler, useMemo, useRef, useState} from 'react';
import Settings from "../../../models/settingsModel";
import {
    AwesomeIcon, ChangeFunction, ChangeKeyFunction, Checkbox, CloseButton, Link, Loader, voidFunction, WithBackground
} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {
    AccessKey, ActivateHandle, EmailFlow, FilenameBase, InvoiceSettingsErrors, ResetErrorHandler
} from "../../../util/interfaces";
import classNames from 'classnames';
import {sendGaEvent} from "../../../util/analytics";
import {BolmateButton, BolmateInput, BolmateNumberInput} from "../../controls";
import {formatInvoiceNumber, replaceDateVariables} from "../../../util/utils";
import Step from "../../email/emailStep/step";
import EmailStep from "../../../models/emailStepModel";
import EmailTemplate from "../../../models/emailTemplateModel";
import TemplateSelectController from "../../email/template/templateSelectController";
import BolmateRadio from "../../controls/radio/bolmateRadio";
import useRight from "../../misc/useRight";
import InvoiceUploadSettingsController from "../../invoiceUploadSettings/invoiceUploadSettingsController";

interface Props {
    onChangeSetting: ChangeKeyFunction<string>;
    onChangeInvoicePreview: ChangeFunction<string>;
    onResetError: ResetErrorHandler<InvoiceSettingsErrors>;
    onSubmit: MouseEventHandler;
    onReplaceCreditEmail: (step: EmailStep) => void;
    onChangeCheck: ChangeKeyFunction;
    onChangeDifferNumbers: ChangeFunction<boolean>;
    onChangeFilenameBase: ChangeKeyFunction<FilenameBase>;
    onNavigateTemplate: (template: EmailTemplate, step: EmailStep, returnPage?: string) => void;
    settings: Settings;
    errors: InvoiceSettingsErrors;
    loading: boolean;
    hasChanged: boolean;
    invoicePreview: string;
    creditEmailStep: EmailStep;
    emailTemplates: EmailTemplate[];
    differNumbers: boolean;
    productsCount: number;
}

export default function InvoiceSettings(props: Props) {
    const [canEdit] = useRight(AccessKey.INVOICE_SETTINGS_EDIT);
    const {
        settings, onChangeSetting, errors, onResetError, onChangeInvoicePreview, invoicePreview, hasChanged,
        onSubmit, loading, creditEmailStep, onReplaceCreditEmail, emailTemplates, onChangeCheck, onChangeDifferNumbers,
        differNumbers, onChangeFilenameBase, onNavigateTemplate, productsCount
    } = props;
    const [infoOpen, setInfoOpen] = useState(false);
    const templateRef = useRef<ActivateHandle>(null);

    const getDayItems = useMemo(() => {
        return [
            {value: 0, label: "Retour verwerkt"},
            {value: 1, label: "1 dag na verwerking"},
            {value: 3, label: "3 dagen na verwerking"},
            {value: 7, label: "7 dagen na verwerking"},
            {value: 14, label: "14 dagen na verwerking"},
            {value: 'custom', label: 'Aangepast', inputLabel: `${creditEmailStep.days} dagen na verwerking`}
        ]
    }, [creditEmailStep]);

    function onClickTemplate() {
        templateRef.current?.activate(creditEmailStep);
    }

    function onOpenInfo() {
        setInfoOpen(true);
    }

    function onCloseInfo() {
        setInfoOpen(false);
    }

    function renderSettings() {
        const year = IDate.now().year();
        return (
            <div className='invoice-settings-container'>
                <div className='settings-part'>
                    <label className='settings-label'>Factuurnummering</label>
                    <div>
                        <label className='settings-sub-title'>Met de tekstcode {"{jaar}"} vullen we automatisch het
                            juiste jaartal in.</label>
                        <div className='row'>
                            <BolmateInput value={settings.invoicePrefix} onChange={onChangeSetting('invoice_prefix')}
                                          label='Voorvoegsel' error={errors.invoicePrefix}
                                          onFocus={onResetError('invoicePrefix')} disabled={!canEdit}/>
                            <BolmateNumberInput value={settings.invoiceStart}
                                                onChange={onChangeSetting('invoice_start')}
                                                label='Starten met' error={errors.invoiceStart}
                                                onFocus={onResetError('invoiceStart')} disabled={!canEdit} wholeNumbers
                                                disableSymbols/>
                        </div>
                        <div className='row'>
                            <label className='row-label'>Voorbeeld</label>
                            {renderInvoiceNumberPreview()}
                        </div>
                    </div>
                    {renderFilenameBase()}
                    <BolmateButton onClick={onSubmit} label='Opslaan' disabled={loading || !hasChanged || !canEdit}
                                   className='middle-submit'/>
                </div>
                <div className='div'/>
                <div className='settings-part'>
                    <InvoiceUploadSettingsController noSubTitle/>
                </div>
                <div className='div'/>
                <div className='settings-part'>
                    <div>
                        <label className='settings-label'>Creditfactuur instellingen</label>
                        <div className='info-container'>
                            <AwesomeIcon name='info-circle' className='info-icon'/>
                            <span className='info-text'>De norm is dat de creditfactuur de nummerreeks
                            van de gewone factuur volgt. Met deze optie kun je ervoor zorgen dat de creditfactuur
                            een aparte nummerreeks volgt. Bijvoorbeeld {year}-credit-0001</span>
                        </div>
                    </div>
                    {renderCreditInvoiceNumber()}
                    {renderMailSettings()}
                </div>
            </div>
        )
    }

    function renderFilenameBase() {
        const {filenameBase} = settings;
        return (
            <div className='filename-base'>
                <label className='settings-title'>Bestandsnaam baseren op</label>
                <div className='radio-container'>
                    <BolmateRadio onSelect={onChangeFilenameBase(FilenameBase.INVOICE_NUMBER)}
                                  selected={filenameBase === FilenameBase.INVOICE_NUMBER} label='Factuurnummer'
                                  disabled={!canEdit}/>
                    <BolmateRadio onSelect={onChangeFilenameBase(FilenameBase.ORDER_ID)}
                                  selected={filenameBase === FilenameBase.ORDER_ID} label='Ordernummer'
                                  disabled={!canEdit}/>
                </div>
                <div className='row'>
                    <label className='row-label'>Voorbeeld</label>
                    {renderFilenamePreview()}
                </div>
            </div>
        )
    }

    function renderFilenamePreview() {
        const {filenameBase, invoiceStart, invoicePrefix} = settings;
        let preview: string;
        if (filenameBase === FilenameBase.INVOICE_NUMBER)
            preview = replaceDateVariables(invoicePrefix) + invoiceStart;
        else if (filenameBase === FilenameBase.ORDER_ID)
            preview = '1234567890';
        else
            preview = `${replaceDateVariables(invoicePrefix)}${invoiceStart}-1234567890`;
        preview = preview.replace(/ /g, '');
        return <label className='filename-base-preview'>{preview}.pdf</label>;
    }

    function renderInvoiceNumberPreview() {
        const {invoicePrefix, invoiceStart} = settings;
        const count = Number(invoicePreview);
        const editable = !Number.isNaN(count);
        const onUp = () => {
            sendGaEvent('Click', 'Factuur instellingen', 'Nummer voorbeeld +');
            const nextCount = count + 1;
            const nextNum = formatInvoiceNumber(nextCount, invoiceStart);
            onChangeInvoicePreview(nextNum);
        }, onDown = () => {
            sendGaEvent('Click', 'Factuur instellingen', 'Nummer voorbeeld -');
            const nextCount = count - 1;
            const nextNum = formatInvoiceNumber(nextCount, invoiceStart);
            onChangeInvoicePreview(nextNum);
        }
        const prefix = replaceDateVariables(invoicePrefix),
            min = Number(settings.invoiceStart);
        return (
            <div className='invoice-number-preview'>
                <label className='preview'>{prefix}{invoicePreview}</label>
                {editable && (
                    <div className='editors'>
                        <span className='editor' onClick={onUp}>+</span>
                        <span className={classNames('editor', count <= min && 'disabled')}
                              onClick={count > min ? onDown : voidFunction}>-</span>
                    </div>
                )}
            </div>
        );
    }

    function renderCreditInvoiceNumber() {
        const {creditInvoiceNumPrefix, creditInvoiceNumStart} = settings;
        return (
            <div className='credit-invoice-number'>
                <div className='row' style={{display: 'flex', flexDirection: 'column'}}>
                    <Checkbox checked={differNumbers} onCheck={onChangeDifferNumbers} disabled={!canEdit}
                              label='Aparte factuurnummerreeks voor creditfacturen gebruiken'/>
                </div>
                <div className='row'>
                    <BolmateInput value={creditInvoiceNumPrefix} onChange={onChangeSetting('credit_invoice_num_prefix')}
                                  label='Voorvoegsel' disabled={!differNumbers || !canEdit}/>
                    <BolmateNumberInput value={creditInvoiceNumStart}
                                        onChange={onChangeSetting('credit_invoice_num_start')}
                                        label='Starten met' wholeNumbers disabled={!differNumbers || !canEdit}/>
                </div>
            </div>
        )
    }

    function renderMailSettings() {
        const {autoCreateCreditInvoices, autoSendCreditInvoices} = settings;
        return (
            <div className='credit-mail-settings'>
                <div className='header-container'>
                    <label className='settings-title'>Automatiseren</label>
                    <Link onClick={onOpenInfo} className='info-btn'>Meer informatie</Link>
                </div>
                <div className='credit-auto-settings'>
                    <Checkbox label='Creditfacturen automatisch aanmaken' checked={autoCreateCreditInvoices}
                              onCheck={onChangeCheck('auto_create_credit_invoices')} disabled={!canEdit}/>
                    <Checkbox label='Creditfacturen automatisch verzenden' checked={autoSendCreditInvoices}
                              onCheck={onChangeCheck('auto_send_credit_invoices')}
                              disabled={!autoCreateCreditInvoices || !canEdit}/>
                    <div className='step-container'>
                        <label className='settings-title'>E-mail voor creditfacturen</label>
                        <label className='sub-title'>Deze mail wordt gebruikt voor de verzending van
                            creditfacturen (automatisch & handmatig)</label>
                        <Step onReplaceStep={onReplaceCreditEmail} step={creditEmailStep} templates={emailTemplates}
                              showWarning={false} className='credit-step' dayItems={getDayItems}
                              onClickTemplate={onClickTemplate} onNavigateTemplate={onNavigateTemplate}
                              productsCount={productsCount}/>
                        <TemplateSelectController onReplaceStep={onReplaceCreditEmail} ref={templateRef}
                                                  returnPage='/instellingen/factuur-instellingen'
                                                  flows={[EmailFlow.CREDIT_INVOICE]}/>
                    </div>
                </div>
            </div>
        )
    }

    function renderInfo() {
        return (
            <WithBackground active={infoOpen} onClose={onCloseInfo}>
                <div className='credit-info default-popup'>
                    <div className='header-container'>
                        <label className='header-title'>Automatische creditfacturen</label>
                        <CloseButton onClick={onCloseInfo}/>
                    </div>
                    <div className='body-container'>
                        <p className='info-message'>Bolmate importeert automatisch periodiek al je retourzendingen van
                            de afgelopen 3 maanden. Wanneer een retourzending door jou als verkoper is verwerkt en
                            Bol het bedrag van de retourzending aan je klant heeft terugbetaald kunnen wij
                            automatisch een creditfactuur aanmaken aan de hand van de retourzending. Deze creditfactuur
                            kunnen wij ook direct, of met een vertraging, naar je klant verzenden.<br/><br/>De
                            creditfactuur kan alleen automatisch aangemaakt worden wanneer de retourzending volledig
                            afgehandeld is en Bol het bedrag aan je klant heeft teruggestort.</p>
                    </div>
                    <div className='footer-container'>
                        <BolmateButton onClick={onCloseInfo} label='Oké'/>
                    </div>
                </div>
            </WithBackground>
        )
    }

    return (
        <div className='invoice-settings'>
            <Loader active={loading}/>
            {renderSettings()}
            {renderInfo()}
            <div className='page-btn-container'>
                <BolmateButton onClick={onSubmit} label='Opslaan' disabled={loading || !hasChanged || !canEdit}/>
            </div>
        </div>
    );
}